/**
 * Lead Filters Component
 * Search, status filter, employee filter, city filter
 * Matches the current LeadsPage filter design
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
};

/**
 * LeadFilters Component
 * 
 * @param {Object} props
 * @param {string} props.search - Current search query
 * @param {function} props.setSearch - Search setter
 * @param {string} props.filterEmployee - Current employee filter (empty string = all)
 * @param {function} props.setFilterEmployee - Employee filter setter
 * @param {string} props.filterStatus - Current status filter (empty string = all)
 * @param {function} props.setFilterStatus - Status filter setter
 * @param {string} props.filterCity - Current city filter (empty string = all)
 * @param {function} props.setFilterCity - City filter setter
 * @param {Array} props.employees - List of employees for dropdown
 * @param {Array} props.statuses - List of statuses for dropdown
 * @param {Array} props.cities - List of cities for dropdown
 * @param {function} props.onReset - Reset callback
 * @param {boolean} props.isSalesExec - Whether current user is Sales Executive
 * @param {string} props.userName - Current user's name (for Sales Exec display)
 */
export const LeadFilters = ({
  search = '',
  setSearch,
  filterEmployee = '',
  setFilterEmployee,
  filterStatus = '',
  setFilterStatus,
  filterCity = '',
  setFilterCity,
  employees = [],
  employeeCounts = {},
  statuses = [],
  statusCounts = {},
  cities = [],
  cityCounts = {},
  onReset,
  isSalesExec = false,
  userName = 'My Leads',
}) => {
  // Local search state for immediate UI feedback
  const [localSearch, setLocalSearch] = useState(search);
  const [isSearching, setIsSearching] = useState(false);
  
  // Debounce the search value (500ms delay)
  const debouncedSearch = useDebounce(localSearch, 500);
  
  // Sync local search with parent when parent changes (e.g., reset)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);
  
  // Trigger actual search only when debounced value changes and meets minimum length
  useEffect(() => {
    // Only search if: empty (to clear), or 3+ characters
    if (debouncedSearch === '' || debouncedSearch.length >= 3) {
      if (debouncedSearch !== search) {
        setSearch?.(debouncedSearch);
      }
      setIsSearching(false);
    } else {
      setIsSearching(false);
    }
  }, [debouncedSearch, search, setSearch]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    // Show searching indicator only if user is typing and has 3+ chars
    if (value.length >= 3) {
      setIsSearching(true);
    }
  };
  
  const handleReset = () => {
    setLocalSearch('');
    setSearch?.('');
    setFilterEmployee?.('');
    setFilterStatus?.('');
    setFilterCity?.('');
    onReset?.();
  };

  // Handle employee filter change - ensure consistent empty string for "all"
  const handleEmployeeChange = (value) => {
    const newValue = value === 'all' ? '' : value;
    setFilterEmployee?.(newValue);
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    const newValue = value === 'all' ? '' : value;
    setFilterStatus?.(newValue);
  };

  // Handle city filter change
  const handleCityChange = (value) => {
    const newValue = value === 'all' ? '' : value;
    setFilterCity?.(newValue);
  };

  // Get display value for selects (convert empty string to 'all')
  const getSelectValue = (value) => value || 'all';

  return (
    <div className="bg-white rounded-xl border p-4 mb-5">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search Input with debouncing */}
        <div className="flex-1 min-w-[250px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or mobile (min 3 chars)..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
            data-testid="search-input"
          />
          {/* Show loading indicator while debouncing */}
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500 animate-spin" />
          )}
          {/* Show clear button when there's text */}
          {localSearch && !isSearching && (
            <button 
              onClick={() => { setLocalSearch(''); setSearch?.(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {/* Helper text for minimum characters */}
          {localSearch.length > 0 && localSearch.length < 3 && (
            <div className="absolute left-0 -bottom-5 text-xs text-amber-600">
              Type {3 - localSearch.length} more character{3 - localSearch.length > 1 ? 's' : ''} to search
            </div>
          )}
        </div>

        {/* Employee filter - Sales roles only + employees with leads */}
        {isSalesExec ? (
          <div className="flex items-center px-3 h-10 bg-slate-100 rounded-md text-sm font-medium text-slate-700 border">
            {userName}
          </div>
        ) : (
          <Select 
            value={getSelectValue(filterEmployee)} 
            onValueChange={handleEmployeeChange}
          >
            <SelectTrigger className="w-[200px] h-10 bg-white text-sm" data-testid="filter-employee">
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map((emp) => {
                const count = employeeCounts[emp.name];
                return (
                  <SelectItem key={emp.id} value={emp.name}>
                    {emp.name}{count !== undefined ? ` (${count})` : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}

        {/* Status Filter */}
        <Select 
          value={getSelectValue(filterStatus)} 
          onValueChange={handleStatusChange}
        >
          <SelectTrigger className="w-[180px] h-10 bg-white text-sm" data-testid="filter-status">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="all">All Status</SelectItem>
            {statuses.map((s) => {
              const count = statusCounts[s.value];
              return (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}{count !== undefined ? ` (${count})` : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* City Filter */}
        <Select 
          value={getSelectValue(filterCity)} 
          onValueChange={handleCityChange}
        >
          <SelectTrigger className="w-[160px] h-10 bg-white text-sm" data-testid="filter-city">
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map((c) => {
              const count = cityCounts[c];
              return (
                <SelectItem key={c} value={c}>
                  {c}{count !== undefined ? ` (${count})` : ''}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Reset Button */}
        <button 
          onClick={handleReset}
          className="px-3 py-2.5 border rounded-lg hover:bg-gray-50 font-medium text-sm flex items-center gap-1"
          data-testid="reset-filters-btn"
        >
          <X className="h-4 w-4" /> Reset
        </button>
      </div>
    </div>
  );
};

export default LeadFilters;
